import { useEffect, useMemo, useState } from "react";
import RestaurantDropdown from "../../Utils/RestaurantDropdown";

import {
  X,
  Store,
  Route,
  IndianRupee,
  Save,
  Loader2,
  Pen,
  Trash2,
  Plus,
  Layers3,
  RotateCcw,
} from "lucide-react";

const EMPTY_FORM = {
  minimumDistance: "0",
  maximumDistance: "0",
  minimumOrderAmount: "0",
  maximumOrderAmount: "300",
  deliveryCharges: "30",
  perKmCharges: "",
};

const NumberField = ({
  label,
  field,
  value,
  icon: Icon,
  disabled = false,
  onValueChange,
}) => (
  <div>
    <label className="mb-1 block text-[11px] font-medium text-gray-600">
      {label}
    </label>

    <div className="relative">
      <Icon
        size={13}
        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
      />

      <input
        type="number"
        min="0"
        step="any"
        value={value}
        disabled={disabled}
        onChange={(e) => onValueChange(field, e.target.value)}
        className={`h-9 w-full rounded-lg border pl-8 pr-2 text-xs outline-none transition [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500"
            : "border-gray-200 bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        }`}
      />
    </div>
  </div>
);

const DeliveryChargeModal = ({
  open,

  // when opened directly from Shop List
  shop = null,

  // undefined = user can select shop
  // null = global group
  // "A1001" = specific restaurant
  restaurantId = undefined,

  shops = [],
  deliveryCharges = [],

  saving = false,
  deletingId = null,

  onClose,
  onSave,
  onDelete,
}) => {
  const [selectedUserId, setSelectedUserId] = useState("");

  const [editingCharge, setEditingCharge] = useState(null);

  const [pendingDeleteCharge, setPendingDeleteCharge] = useState(null);

  const [formData, setFormData] = useState(EMPTY_FORM);

  // =========================================================
  // INITIAL TARGET
  // =========================================================

  useEffect(() => {
    if (!open) return;

    let targetUserId = "";

    if (shop?.shop_code) {
      targetUserId = String(shop.shop_code);
    } else if (restaurantId !== undefined && restaurantId !== null) {
      targetUserId = String(restaurantId);
    }

    setSelectedUserId(targetUserId);

    setEditingCharge(null);
    setPendingDeleteCharge(null);

    setFormData(EMPTY_FORM);
  }, [open, shop?.shop_code, restaurantId]);

  // =========================================================
  // LOCK SHOP SELECTOR?
  // =========================================================

  const isShopLocked = Boolean(shop?.shop_code) || restaurantId !== undefined;

  // =========================================================
  // FIND SELECTED SHOP
  // =========================================================

  const selectedShop = useMemo(() => {
    if (!selectedUserId) return null;

    return (shops || []).find(
      (item) => String(item?.shop_code) === String(selectedUserId),
    );
  }, [shops, selectedUserId]);

  // =========================================================
  // FIND EXISTING GROUP / SLABS
  // =========================================================

  const existingSlabs = useMemo(() => {
    const group = (deliveryCharges || []).find((item) => {
      const apiRestaurantId = item?.restaurant_id;

      // GLOBAL
      if (!selectedUserId) {
        return (
          apiRestaurantId === null ||
          apiRestaurantId === undefined ||
          apiRestaurantId === ""
        );
      }

      return String(apiRestaurantId) === String(selectedUserId);
    });

    return [...(group?.deliveryDetails || [])].sort(
      (a, b) => Number(a?.minimum_amount || 0) - Number(b?.minimum_amount || 0),
    );
  }, [deliveryCharges, selectedUserId]);

  // =========================================================
  // HELPERS
  // =========================================================

  const formatNumber = (value) => {
    const number = Number(value || 0);

    return Number.isInteger(number) ? number : number.toFixed(2);
  };

  const formatRange = (minimum, maximum, prefix = "", suffix = "") => {
    const min = Number(minimum || 0);
    const max = Number(maximum || 0);

    if (max <= 0) {
      return `${prefix}${formatNumber(min)}+${suffix}`;
    }

    return `${prefix}${formatNumber(min)} - ${prefix}${formatNumber(
      max,
    )}${suffix}`;
  };

  const resetForm = () => {
    setEditingCharge(null);
    setFormData(EMPTY_FORM);
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // =========================================================
  // EDIT SLAB
  // =========================================================

  const handleEdit = (slab) => {
    setEditingCharge(slab);

    setFormData({
      minimumDistance: String(slab?.minimum_distance ?? "0"),

      maximumDistance: String(slab?.maximum_distance ?? "0"),

      minimumOrderAmount: String(slab?.minimum_amount ?? "0"),

      maximumOrderAmount: String(slab?.maximum_amount ?? "0"),

      deliveryCharges: String(slab?.delivery_charges ?? ""),

      perKmCharges: String(slab?.per_km_charges ?? ""),
    });
  };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSubmit = async () => {
    if (
      String(formData.deliveryCharges).trim() === "" ||
      String(formData.perKmCharges).trim() === ""
    ) {
      return;
    }

    const success = await onSave?.(
      {
        userId: selectedUserId,

        minimumDistance: formData.minimumDistance,

        maximumDistance: formData.maximumDistance,

        minimumOrderAmount: formData.minimumOrderAmount,

        maximumOrderAmount: formData.maximumOrderAmount,

        deliveryCharges: formData.deliveryCharges,

        perKmCharges: formData.perKmCharges,
      },
      Boolean(editingCharge),
    );

    if (success) {
      resetForm();
    }
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = (slab) => {
    setPendingDeleteCharge(slab);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteCharge) return;

    const slab = pendingDeleteCharge;

    const success = await onDelete?.({
      userId: selectedUserId,
      chargeId: slab.pk_charges_id,
    });

    if (success) {
      if (String(editingCharge?.pk_charges_id) === String(slab.pk_charges_id)) {
        resetForm();
      }

      setPendingDeleteCharge(null);
    }
  };

  if (!open) return null;

  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-3">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Manage Delivery Charges
            </h2>

            <p className="mt-0.5 text-[11px] text-gray-500">
              Add, edit or remove delivery slabs.
            </p>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={() => {
              setPendingDeleteCharge(null);
              onClose?.();
            }}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        {/* =====================================================
            BODY
        ===================================================== */}

        <div className="flex-1 overflow-y-auto p-4">
          {/* ===================================================
              SHOP
          =================================================== */}

          <div className="mb-4">
            <label className="mb-1 block text-[11px] font-medium text-gray-600">
              Shop / Restaurant
            </label>

            <div className="relative">
              {isShopLocked ? (
                <>
                  <Store
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    readOnly
                    value={
                      selectedUserId
                        ? `${selectedShop?.shop_name || "Shop"} (${selectedUserId})`
                        : "Default / Global Delivery Charges"
                    }
                    className="h-9 w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 pl-8 pr-3 text-xs text-gray-600 outline-none"
                  />
                </>
              ) : (
                <RestaurantDropdown
                  shops={shops}
                  value={selectedUserId}
                  onChange={(shopCode) => {
                    setSelectedUserId(shopCode);
                    resetForm();
                  }}
                  placeholder="Default / Global"
                />
              )}
            </div>
          </div>

          {/* ===================================================
              EXISTING SLABS
          =================================================== */}

          <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50/70">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
              <div className="flex items-center gap-2">
                <Layers3 size={14} className="text-orange-500" />

                <h3 className="text-xs font-semibold text-gray-800">
                  Existing Slabs
                </h3>
              </div>

              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-gray-500">
                {existingSlabs.length} slab
                {existingSlabs.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="max-h-[210px] overflow-y-auto p-2">
              {existingSlabs.length === 0 ? (
                <div className="py-5 text-center text-[11px] text-gray-400">
                  No delivery slabs configured.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {existingSlabs.map((slab) => {
                    const isDeleting =
                      String(deletingId) === String(slab.pk_charges_id);

                    const isEditing =
                      String(editingCharge?.pk_charges_id) ===
                      String(slab.pk_charges_id);

                    return (
                      <div
                        key={slab.pk_charges_id}
                        className={`rounded-lg border bg-white p-2.5 transition ${
                          isEditing
                            ? "border-orange-400 ring-1 ring-orange-100"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          {/* Information */}
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] font-semibold text-gray-900">
                                {formatRange(
                                  slab.minimum_amount,
                                  slab.maximum_amount,
                                  "₹",
                                )}
                              </span>

                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-500">
                                {formatRange(
                                  slab.minimum_distance,
                                  slab.maximum_distance,
                                  "",
                                  " KM",
                                )}
                              </span>
                            </div>

                            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px]">
                              <span className="font-medium text-green-600">
                                Delivery ₹{formatNumber(slab.delivery_charges)}
                              </span>

                              <span className="text-gray-300">•</span>

                              <span className="font-medium text-blue-600">
                                ₹{formatNumber(slab.per_km_charges)}
                                /KM
                              </span>
                            </div>

                            <p className="mt-1 text-[9px] text-gray-400">
                              Charge ID #{slab.pk_charges_id}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex shrink-0 gap-1">
                            <button
                              type="button"
                              onClick={() => handleEdit(slab)}
                              disabled={saving || isDeleting}
                              className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-blue-600 transition hover:bg-blue-100 disabled:opacity-40"
                              title="Edit slab"
                            >
                              <Pen size={12} />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(slab)}
                              disabled={saving || isDeleting}
                              className="flex h-7 w-7 items-center justify-center rounded-md bg-red-50 text-red-600 transition hover:bg-red-100 disabled:opacity-40"
                              title="Delete slab"
                            >
                              {isDeleting ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Trash2 size={12} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ===================================================
              ADD / EDIT FORM
          =================================================== */}

          <div className="rounded-xl border border-gray-200">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <div>
                <h3 className="text-xs font-semibold text-gray-800">
                  {editingCharge
                    ? `Edit Slab #${editingCharge.pk_charges_id}`
                    : "Add New Slab"}
                </h3>

                {editingCharge && (
                  <p className="mt-0.5 text-[10px] text-gray-500">
                    Only delivery and per KM charges can be updated.
                  </p>
                )}
              </div>

              {editingCharge ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-600 hover:bg-gray-200"
                >
                  <RotateCcw size={11} />
                  Cancel Edit
                </button>
              ) : (
                <div className="flex items-center gap-1 text-[10px] font-medium text-orange-600">
                  <Plus size={11} />
                  New
                </div>
              )}
            </div>

            <div className="p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                <NumberField
                  label="Min Distance"
                  field="minimumDistance"
                  value={formData.minimumDistance}
                  icon={Route}
                  onValueChange={handleChange}
                  disabled={Boolean(editingCharge)}
                />

                <NumberField
                  label="Max Distance"
                  field="maximumDistance"
                  value={formData.maximumDistance}
                  icon={Route}
                  onValueChange={handleChange}
                  disabled={Boolean(editingCharge)}
                />

                <NumberField
                  label="Min Order"
                  field="minimumOrderAmount"
                  value={formData.minimumOrderAmount}
                  icon={IndianRupee}
                  onValueChange={handleChange}
                  disabled={Boolean(editingCharge)}
                />

                <NumberField
                  label="Max Order"
                  field="maximumOrderAmount"
                  value={formData.maximumOrderAmount}
                  icon={IndianRupee}
                  onValueChange={handleChange}
                  disabled={Boolean(editingCharge)}
                />

                <NumberField
                  label="Delivery Charge"
                  field="deliveryCharges"
                  value={formData.deliveryCharges}
                  icon={IndianRupee}
                  onValueChange={handleChange}
                />

                <NumberField
                  label="Per KM Charge"
                  field="perKmCharges"
                  value={formData.perKmCharges}
                  icon={IndianRupee}
                  onValueChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* =====================================================
            FOOTER
        ===================================================== */}

        <div className="flex shrink-0 items-center justify-between border-t bg-white px-4 py-3">
          <span className="text-[10px] text-gray-400">
            {selectedUserId
              ? `Shop: ${selectedUserId}`
              : "Global delivery configuration"}
          </span>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setPendingDeleteCharge(null);
                onClose?.();
              }}
              className="h-9 rounded-lg bg-gray-100 px-4 text-xs font-medium text-gray-600 transition hover:bg-gray-200"
            >
              Close
            </button>

            <button
              type="button"
              disabled={
                saving ||
                String(formData.deliveryCharges).trim() === "" ||
                String(formData.perKmCharges).trim() === ""
              }
              onClick={handleSubmit}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-orange-500 px-4 text-xs font-medium text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {saving ? (
                <Loader2 size={13} className="animate-spin" />
              ) : editingCharge ? (
                <Save size={13} />
              ) : (
                <Plus size={13} />
              )}

              {editingCharge ? "Update Slab" : "Add Slab"}
            </button>
          </div>
        </div>

        {pendingDeleteCharge && (
          <div className="absolute inset-0 z-[110] flex items-center justify-center bg-black/45 p-4">
            <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Delete Delivery Slab?
                </h3>
                <p className="mt-1 text-[11px] leading-4 text-gray-500">
                  Review the slab details before confirming. This action cannot
                  be undone.
                </p>
              </div>

              <div className="space-y-3 p-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-gray-50 p-2.5">
                    <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">
                      Order Range
                    </p>
                    <p className="mt-1 text-xs font-semibold text-gray-800">
                      {formatRange(
                        pendingDeleteCharge.minimum_amount,
                        pendingDeleteCharge.maximum_amount,
                        "₹",
                      )}
                    </p>
                  </div>

                  <div className="rounded-lg bg-gray-50 p-2.5">
                    <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">
                      Distance Range
                    </p>
                    <p className="mt-1 text-xs font-semibold text-gray-800">
                      {formatRange(
                        pendingDeleteCharge.minimum_distance,
                        pendingDeleteCharge.maximum_distance,
                        "",
                        " KM",
                      )}
                    </p>
                  </div>

                  <div className="rounded-lg bg-green-50 p-2.5">
                    <p className="text-[9px] font-medium uppercase tracking-wide text-green-500">
                      Delivery Charge
                    </p>
                    <p className="mt-1 text-xs font-semibold text-green-700">
                      ₹{formatNumber(pendingDeleteCharge.delivery_charges)}
                    </p>
                  </div>

                  <div className="rounded-lg bg-blue-50 p-2.5">
                    <p className="text-[9px] font-medium uppercase tracking-wide text-blue-500">
                      Per KM Charge
                    </p>
                    <p className="mt-1 text-xs font-semibold text-blue-700">
                      ₹{formatNumber(pendingDeleteCharge.per_km_charges)}/KM
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                  <p className="text-[10px] font-medium text-red-600">
                    Charge ID #{pendingDeleteCharge.pk_charges_id}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t px-4 py-3">
                <button
                  type="button"
                  onClick={() => setPendingDeleteCharge(null)}
                  disabled={deletingId !== null}
                  className="h-9 rounded-lg bg-gray-100 px-4 text-xs font-medium text-gray-600 transition hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={
                    String(deletingId) ===
                    String(pendingDeleteCharge.pk_charges_id)
                  }
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-red-600 px-4 text-xs font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {String(deletingId) ===
                  String(pendingDeleteCharge.pk_charges_id) ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryChargeModal;
